// User Settings Definition - Structure, defaults, and configuration for user settings
// These settings persist across app restarts and are user/device-specific

import { ExportConfig, ExportFormat, ExportTheme, ExportPageSize, ExportMargins } from "./models";

// User settings structure - stored locally in user_config.json (Electron) or /users/me (Web)
export interface UserSettings {
    theme: string;
    defaultModel: string;
    notifications: {
        generationComplete: boolean;
    };
    export_options: {
        format: ExportFormat;
        theme: ExportTheme;
        headerText?: string;
        footerText?: string;
        pageSize?: ExportPageSize;
        landscape?: boolean;
        margins?: ExportMargins;
        // Export configuration
        config: ExportConfig;
        defaultDirectory: string;
    };
}

// Provides default values for user-specific settings.
// Used as initial values if no stored user settings are found.
export const DEFAULT_USER_SETTINGS: UserSettings = {
	theme: 'dark',
	defaultModel: 'EdgeRunner-Light', // superseded by the backend fetched option
	notifications: {
		generationComplete: true,
	},
	export_options: {
		format: 'pdf',
		theme: 'dark',
		pageSize: 'A4',
		landscape: false,
		margins: 'Default',
		config: {
			// Content options
			includeMarkdown: true,
			includeCitations: true,
			includeReasoning: true,
			includeMetadata: true,
			includeTimestamp: false,
			// Display options
			showModel: true,
			showTitle: true,
			showRoleHeader: true,
			// Layout options
			includeHeader: true,
			includeFooter: true,
			// Image options
			includeImages: true,
			embedImages: true,
			imageQuality: 100,
		},
		defaultDirectory: '',
	},
}; 

// Utility type: generates all valid dot-notation paths for an object
// Includes both leaf properties and nested object paths (e.g., 'export_options.config')
type PathsOf<T, Prefix extends string = ''> = T extends object
    ? {
          [K in keyof T & string]: T[K] extends object
              ? `${Prefix}${K}` | PathsOf<T[K], `${Prefix}${K}.`>
              : `${Prefix}${K}`;
      }[keyof T & string]
    : never;

// All valid user setting paths (e.g., 'theme', 'export_options.format')
export type UserSettingPath = PathsOf<UserSettings>;
